import BacktestLoader from './components/BacktestLoader';
import BacktestKpis from './components/BacktestKpis';
import EquityChart from './components/EquityChart';

export default function App() {
  return (
    <div className="page">
      <BacktestLoader />

      {/* ================= 1) HERO / ABOVE THE FOLD ================= */}
      {/* ================= 1) HERO / ABOVE THE FOLD ================= */}
      <section className="heroFull">
        <div className="inner">
          <header className="hero heroWide">
            <div className="heroTop">
              <div className="brandPill">SPX Gamma</div>
              <div className="heroMeta">ACCE • 1-DTE • Selective Dislocation • Kelly-Scaled</div>
            </div>

            <h1 className="heroTitle">ACCE — Adaptive Capital Convexity Engine</h1>

            <p className="heroSubtitle">
              A selective 1-DTE SPX iron condor strategy built to accelerate account growth when market structure creates a
              high-conviction premium-selling opportunity.
            </p>

            <div className="heroBody">
              <p className="heroLead">
                <strong>ACCE (Adaptive Capital & Convexity Engine)</strong> is the selective growth engine inside the broader{' '}
                <strong>SPX Gamma Premium Sellers strategy suite</strong>. Where <strong>MEIC</strong> focuses on intraday
                0-DTE iron condor opportunities, <strong>GEMIC</strong> provides systematic daily 1-DTE expected-move iron
                condors, and <strong>ARGOS</strong> adapts across multi-day regime windows, ACCE is designed to wait for
                higher-impact 1-DTE dislocations where structure, volatility, and dealer positioning align.
              </p>

              <div style={{ height: 5 }} />

              <p className="heroLead">
                ACCE does not issue trades every day. It is intentionally selective. The strategy evaluates SPX price movement
                against adaptive expected-move bands, then applies additional filters tied to volatility behavior, option-flow
                context, and market structure. A large move alone is not enough. ACCE only deploys capital when the data
                supports a defined-risk mean-reversion opportunity.
              </p>

              <div style={{ height: 5 }} />

              <div style={{ height: 5 }} />

              <p className="heroLead">
                Inside the larger SPX Gamma framework, ACCE serves as the aggressive compounding layer. It uses{' '}
                <strong>portfolio-level, Kelly-based scaling logic</strong> to increase exposure when equity grows and reduce
                exposure when drawdowns occur. Risk remains capped by construction, but position size adapts to the account
                path. The goal is intentional acceleration: turning a selective 1-DTE edge into meaningful capital growth over
                time.
              </p>
            </div>

            <div className="heroActions">
              <a className="btnPrimary" href="#performance">View Performance</a>
              <a className="btnGhost" href="https://www.spxgamma.com/" target="_blank" rel="noopener noreferrer">
                Access SPX Gamma
              </a>
            </div>
          </header>
        </div>
      </section>

      {/* ================= 2) QUICK SUMMARY STRIP (LESS BOXY) ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="paperStrip">
            <div className="stripItem">
              <div className="stripCallout">Designed For</div>
              <p className="text">
                Designed for ~$10,000 accounts ($5,000 minimum equity required) seeking a rules-based path to aggressive compounding
                — systematically increasing size only when market structure supports accelerated capital growth.
              </p>
            </div>

            <div className="stripItem">
              <div className="stripCallout">Approach</div>
              <p className="text">
                <strong>1-DTE SPX Iron Condor</strong>, deployed selectively when structure,
                volatility, and dealer positioning align. Defined-risk by construction,
                low frequency by design, and <strong>scaled aggressively</strong> to compound
                capital during the growth phase.
              </p>
            </div>

            <div className="stripItem">
              <div className="stripCallout">Objective</div>
              <p className="text">
                Path-dependent growth engineered through infrequent, high-impact capital deployment when structure
                decisively favors mean reversion.
              </p>
            </div>

            <div className="stripCallout">
              ACCE is a capital deployment engine built to grow a trading account into a diversified portfolio over time. This is not a “trade every day” strategy —
              it is a portfolio-building framework designed for long-term wealth creation.
            </div>
          </div>
        </div>
      </section>


      {/* ================= 9) PERFORMANCE (DYNAMIC: STATS STRIP + FEATURE CHART) ================= */}
      <section id="performance" className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Performance</h2>
          </div>

          <div className="lede">
            <p className="text">
              These results reflect the real trade sequence and highlight why the ACCE 1 DTE Iron Condor is built around selective deployment and sequential scaling. By only entering when conditions align and
              adjusting exposure trade-by-trade as equity evolves, the system can press advantage during favorable runs while automatically reducing risk during drawdowns.  The outcome is a more resilient compounding
              profile—where growth is driven by disciplined participation and controlled sizing, not constant exposure.
            </p>
          </div>

          {/*<div className="statStrip">*/}
          {/*  <div className="stat">*/}
          {/*    <div className="statLabel">Starting Equity</div>*/}
          {/*    <div className="statValue">$10,000</div>*/}
          {/*  </div>*/}
          {/*  <div className="stat">*/}
          {/*    <div className="statLabel">Ending Equity</div>*/}
          {/*    <div className="statValue">$170,669</div>*/}
          {/*  </div>*/}
          {/*  <div className="stat">*/}
          {/*    <div className="statLabel">Trade Days</div>*/}
          {/*    <div className="statValue">140</div>*/}
          {/*  </div>*/}
          {/*  <div className="stat">*/}
          {/*    <div className="statLabel">Max Drawdown</div>*/}
          {/*    <div className="statValue">30.8%</div>*/}
          {/*  </div>*/}
          {/*</div>*/}

          <div className="feature">
            {/*<div className="featureHeader">*/}
            {/*  <div className="featureKicker">Primary Evidence</div>*/}
            {/*  <div className="featureTitle">Equity Curve</div>*/}
            {/*  <div className="featureSub">Scaled • Backtest + Live</div>*/}
            {/*</div>*/}

            <div className="featureBody">
              <EquityChart />
            </div>
          </div>
        </div>
      </section>


      {/* ================= 4) STRATEGY OVERVIEW (WHITEPAPER FLOW) ================= */}
      <section className="sectionFull">
        <div className="inner">

          <div className="sectionHeader">
            <h2 className="sectionTitle">Strategy Overview</h2>
          </div>

          {/* Card 1 */}
          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">Strategy Snapshot</h3>

              <p className="text">
                ACCE operates exclusively in SPX using defined-risk 1-DTE iron condors. Trades are entered selectively
                at the market close and held through expiration the following trading day. Each structure targets a
                minimum credit of <strong>$1.70</strong>, with a maximum loss of approximately
                <strong> $330</strong> per structure.
              </p>

              <p className="text">
                Trade selection is driven by a layered probability framework. Volatility is assessed using a
                multi-input model incorporating <strong>VIX</strong>,{' '}
                <strong>VIX1D</strong>, and <strong>VVIX</strong>{' '}
                to contextualize both price movement and volatility stability. Trade decisions are further filtered
                through option flow dynamics and regime context to ensure capital is deployed only when structural
                conditions favor mean reversion.
              </p>


              <p className="text">
                Position sizing is sequential and capital-aware, not fixed. ACCE employs
                <strong> a path-dependent Kelly-based scaling</strong>, recalculating exposure after every trade relative to
                the initial starting capital and the evolving equity curve. Because sizing responds to the sequence of outcomes—not just total returns—the strategy compounds during favorable runs while automatically scaling down during drawdowns, enforcing disciplined growth and capital protection over time.
                <strong> Each user must follow their own sizing path exactly, as deviations materially change risk and outcomes.</strong>
              </p>

              <div className="stripCallout">
                Across historical testing, ACCE has achieved a trade success rate exceeding <strong>79%</strong>,
                reflecting its emphasis on selective deployment, defined risk, and probabilistic edge.
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">The Core Concept</h3>

              <p className="text">
                ACCE is built on a simple but critical observation: large price moves only create opportunity when
                volatility structure supports reversion.
              </p>

              <p className="text">
                Most large SPX moves are not tradable. They occur in unstable volatility regimes, during directional
                expansion, or under option-flow conditions that invalidate mean-reversion assumptions. ACCE exists to
                separate tradable dislocations from noise. Instead of trading every day, the system waits for statistically abnormal SPX price movement relative to
                adaptive expected-move bands. When such a move occurs, ACCE applies an additional proprietary filtering
                layer based on volatility structure and option flow behavior. Only when price dislocation and structural confirmation align does the engine deploy capital using
                defined-risk trades sized according to portfolio growth rules.
              </p>

                <div style={{ height: 10 }} />

              <p className="stripCallout">
                The result is a system designed to generate exponential equity behavior from infrequent but structurally
                high-impact deployments.
              </p>
            </div>
          </div>

        </div>
      </section>


      {/* ================= 6) MONTE CARLO EARLY + IMAGE BREAK ================= */}
      <section className="sectionFull sectionBand">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Monte Carlo Validation</h2>
          </div>

          {/* Force 50/50 split for this section only */}
          <div className="split split--mc">
            <div className="splitLeft">
              <p className="text">
                Because ACCE scales exposure, performance is inherently path-dependent. The order of wins and losses
                directly changes future position sizing — meaning the same set of trades can produce materially different
                equity curves depending on sequencing.
              </p>

              <p className="text">
                To validate sustainability, we ran <strong>10,000 Monte Carlo simulations</strong> that randomize the order
                of the historical trade outcomes and replay the strategy across thousands of possible equity paths. This is
                how you test whether a scaling model survives adverse sequences — not just whether it looks good in a single
                backtest line.
              </p>

              <p className="text">
                Assumptions: Baseline modeling uses a <strong>$10,000</strong> starting account with an initial size of
                <strong> 4 contracts</strong>, a maximum cap of <strong>20 contracts</strong>, strict defined-risk limits,
                and fees/slippage included. For smaller portfolios, sizing is adjusted proportionally—e.g., a <strong> $5,000</strong>
                starting account begins at <strong> 3 contracts </strong> to maintain comparable risk exposure.

                <div style={{ height: 10 }} />

                Monte Carlo analysis highlights the importance of this adjustment: starting at <strong>$5,000 with 3
                contracts</strong> resulted in a <strong>3.5% failure rate</strong> (350 of 10,000 simulations ending in
                full equity loss), whereas the <strong>$10,000 / 4-contract</strong> sequence produced a significantly
                lower failure rate of <strong>0.11%</strong> (11 of 10,000 simulations).
              </p>

              <div className="callout soft">
                <div className="mutedLine">
                  <strong>Backtest (no scaling):</strong> $10,000 → <strong>$22,307</strong> (<strong>+123.07%</strong>) over 224 trades
                </div>
                <div style={{ height: 10 }} />
                <div className="mutedLine" style={{ marginTop: 6 }}>
                  <strong>Same trades with Kelly scaling:</strong> $10,000 → <strong>$211,426</strong> (<strong>+2114.26%</strong>)
                </div>
                <div style={{ height: 10 }} />
                <div className="mutedLine" style={{ marginTop: 6 }}>
                  <strong>Monte Carlo outcomes:</strong><br />
                  <div style={{ height: 10 }} />
                  Poor trade ordering still produced six-figure results.<br />
                  <div style={{ height: 10 }} />
                  Typical paths clustered around <strong>$220,000</strong>.<br />
                  <div style={{ height: 10 }} />
                  Favorable sequences exceeded <strong>$229,000</strong>.<br />
                  <div style={{ height: 10 }} />
                  Scaling remained profitable across the vast majority of simulations.
                </div>
                <div className="stripCallout" style={{ marginTop: 6 }}>
                  <strong>Failure rate:</strong> <strong>11 / 10,000</strong> paths (<strong>0.11%</strong>) reached the failure floor
                </div>
              </div>

               <div style={{ height: 10 }} />

              <p className="stripCallout">
                The takeaway is simple: scaling can create dramatically higher compounded returns, but it must be proven
                against worst-case sequencing. The percentile bands quantify dispersion, drawdowns, and survivability — the
                real tests of a path-dependent deployment engine.
              </p>
            </div>

            <div className="splitRight">
              <figure className="figure">
                <img
                  className="figureImg"
                  src="/Final%20MC%20Path.png"
                  alt="Monte Carlo equity paths (scaled sizing)"
                  style={{ width: '100%' }}
                />

                <figcaption className="stripCallout">
                  Monte Carlo equity paths using scaled sizing. Randomized sequencing exposes drawdowns and shows the full
                  distribution of terminal outcomes.
                </figcaption>

                <div className="figureDisclaimer">
                  Historical results are shown for illustrative purposes only and do not guarantee future returns.
                </div>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 7) TRIGGER + RISK (COMBINED ROW) ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Trade Trigger Logic</h2>
          </div>

          <div className="grid2">
            {/* LEFT: Condensed Trigger Pipeline (single card) */}
            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Trigger Pipeline</div>
                <div className="paperTag">3:55 PM ET → Decision</div>
              </div>

              <div className="filterStack">
                <div className="filterItem">
                  <div className="filterIndex">1</div>
                  <div className="filterBody">
                    <div className="filterTitle">Event Detection</div>
                    <div className="filterText">
                      SPX close is evaluated against adaptive expected-move bands derived from the prior session.
                      A close outside the bands flags statistically abnormal price displacement.
                    </div>
                  </div>
                </div>

                <div className="filterItem">
                  <div className="filterIndex">2</div>
                  <div className="filterBody">
                    <div className="filterTitle">Volatility Context</div>
                    <div className="filterText">
                      SPX Gamma’s proprietary volatility assessment confirms stability, compression, and regime alignment
                      to determine whether mean reversion is structurally viable.
                    </div>
                  </div>
                </div>

                <div className="filterItem">
                  <div className="filterIndex">3</div>
                  <div className="filterBody">
                    <div className="filterTitle">Eligibility Filtering</div>
                    <div className="filterText">
                      Option flow behavior and rejection rules eliminate structurally poor mean-reversion setups.
                      If any layer fails, the system stands down.
                    </div>
                  </div>
                </div>

                <div className="filterDecision">
                  <div className="filterDecisionTitle">Decision</div>
                  <div className="stripCallout">
                    Event detected ≠ trade signal. Only “Trade Eligible” sessions proceed to entry.
                  </div>
                </div>

                <div className="stripCallout">
                  <span className="stripCallout">Outcome:</span>{' '}
                  If eligible, enter the defined-risk 1-DTE iron condor at the close. If not, stay sidelined.
                </div>
              </div>
            </div>

            {/* RIGHT: Risk & Sizing (moved here as a card) */}
            {/* RIGHT: Risk & Position Management */}
            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Risk & Position Management</div>
                <div className="paperTag">Defined Risk + Scaling</div>
              </div>

              <p className="text" style={{ marginBottom: 10 }}>
                ACCE trades are built on defined-risk 1-DTE iron condors in SPX. Every deployment is structured to collect at least
                <strong>{' '} $1.70</strong> in credit per structure, with a maximum theoretical loss of approximately
                <strong>{' '} $330</strong>. When fills exceed the minimum credit, effective risk drops further.
              </p>

              {/* Key Specs (lighter than stripCallout) */}
             {/* Key spec strip */}
              <div className="stripCallout" style={{ marginTop: 8, marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <strong>Credit Target:</strong> $1.70 minimum
                  </div>
                  <div>
                    <strong>Max Loss:</strong> ~ $330 / structure
                  </div>
                  <div>
                    <strong>Start Size:</strong> 4 contracts
                  </div>
                  <div>
                    <strong>Hard Cap:</strong> 20 contracts
                  </div>
                </div>
              </div>

              <p className="text" style={{ marginBottom: 10 }}>
                Risk is fully known before entry. No averaging down. No martingale behavior. No open-ended exposure — losses are
                capped by construction and governed by strict portfolio risk limits.
              </p>

              <p className="text" style={{ marginBottom: 10 }}>
                Position sizing is adaptive, not fixed. Exposure is determined using portfolio equity, known maximum loss per
                structure, and portfolio-level <strong>Kelly-based scaling logic</strong>. When the edge is present, sizing
                increases; when conditions degrade, sizing contracts — all while enforcing hard caps.
              </p>

              {/* Cap rationale + max loss (integrated, not heavy) */}
              <p className="text" style={{ marginBottom: 10 }}>
                The contract cap exists because gains scale linearly while losses scale non-linearly in short-volatility
                structures. Beyond a certain size, additional exposure increases drawdown risk faster than it improves long-term
                growth.
              </p>

              <div
                className="stripCallout"
                style={{ marginTop: 8 }}
              >
                <strong>Risk ceiling:</strong> at maximum size, a full-loss event is capped at approximately
                <strong>{' '} $6,600</strong> — preserving survivability while still enabling aggressive compounding when conditions align.
              </div>
            </div>


          </div>
        </div>
      </section>

      {/* ================= 10) STRATEGY STATISTICS (FULL-WIDTH) ================= */}
      <section className="sectionFull sectionBand kpiSection">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Strategy Statistics</h2>
            <p className="text">
              These results summarize the full backtest sample. ACCE is a scaling strategy, so the most important numbers
              are growth, expectancy, and drawdown behavior — not a single trade outcome.
            </p>
          </div>

          {/* Headline KPIs */}
          <div className="kpiGrid">
            <div className="kpiCard">
              <div className="kpiLabel">Starting Portfolio</div>
              <div className="kpiValue">$10,000</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Ending Portfolio</div>
              <div className="kpiValue">$221,426</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Net P&amp;L</div>
              <div className="kpiValue">+$211,426</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Total Trades</div>
              <div className="kpiValue">224</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Win Rate</div>
              <div className="kpiValue">79.4%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Expectancy / Trade</div>
              <div className="kpiValue">+$1,147</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Max Drawdown</div>
              <div className="kpiValue">-$28,340</div>
              <div className="kpiSub">(-39.7%)</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Profit Factor</div>
              <div className="kpiValue">1.79</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Max Losing Streak</div>
              <div className="kpiValue">3 trades</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Sharpe (Ann.)</div>
              <div className="kpiValue">2.14</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Sortino (Ann.)</div>
              <div className="kpiValue">1.24</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Calmar</div>
              <div className="kpiValue">3.95</div>
            </div>
          </div>

          <div className="stripCallout" style={{ marginTop: 18 }}>
            ACCE scales exposure as equity grows. That’s why drawdowns and expectancy matter more than any single win/loss.
            Fees and slippage are included in these results.
          </div>
        </div>
      </section>


      {/* ================= 12) FINAL CTA ================= */}
      {/* ================= 12) FINAL CTA ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">This Is the Growth Phase</h2>
          </div>

          <div className="paperCard">
            <p className="text">
              Every serious portfolio has a growth phase. Before income generation, before broader diversification,
              before full premium-selling deployment — capital has to be <strong>built</strong>. Inside the SPX Gamma
              Premium Sellers suite, <strong>ACCE</strong> serves that role: the selective 1-DTE growth engine designed
              to accelerate smaller and mid-sized accounts when market structure creates a high-conviction opportunity.
            </p>

            <p className="text" style={{ marginTop: 10 }}>
              ACCE embraces risk <em>intentionally</em>, not recklessly. Exposure is deployed only when structure,
              volatility, and positioning align, then scaled using portfolio-aware logic so favorable sequences can
              compound more aggressively. Its purpose is not daily participation — it is selective capital deployment
              when the data supports pressing the edge.
            </p>

            <p className="text" style={{ marginTop: 10 }}>
              That is ACCE’s role in the integrated premium-selling suite: build the capital base that allows traders
              to expand into the broader framework over time. GEMIC provides the core daily 1-DTE structure, ARGOS extends
              the suite into adaptive multi-day premium selling, MEIC represents the more capitalized 0-DTE income engine,
              and ACCE helps create the account growth needed to participate in the full system with more flexibility and control.
            </p>

            <div className="heroActions" style={{ marginTop: 16 }}>

              {/*<a className="btnGhost" href="#performance">*/}
              {/*  Study the Growth Curve*/}
              {/*</a>*/}
              <a
                className="btnGhost"
                href="https://www.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore the SPX Gamma Platform
              </a>
            </div>

            <div className="stripCallout" style={{ marginTop: 16 }}>
              Aggressive by design. Selective by necessity. Built to compound first — diversify later.
            </div>
            <div className="figureDisclaimer" style={{ marginTop: 16 }}>
              ACCE trade posts are for educational and informational purposes only. They are not
              financial advice or a recommendation to buy or sell any security or options contract.
              Options trading involves risk and may not be suitable for all traders. Members are
              responsible for their own trade decisions, position sizing, and risk management.
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}







